/**
 * React Query hooks for client asset management.
 *
 * Provides fetching, uploading, deleting, and tag-updating
 * operations for brand assets (logos, avatars).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";
import type { AssetCategory, ClientAssetsResponse } from "../types";

/** Query key factory for asset cache management */
export const assetKeys = {
  all: ["assets"] as const,
  client: (clientId: string) => [...assetKeys.all, clientId] as const,
};

/**
 * Fetch all assets for a client, grouped by category.
 *
 * @param clientId - The client whose assets to fetch
 * @returns Query result with { round, freestyle } arrays
 */
export function useClientAssets(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? assetKeys.client(clientId) : ["assets", "none"],
    queryFn: async (): Promise<ClientAssetsResponse> => {
      if (!clientId) return { round: [], freestyle: [] };
      return apiClient.get<ClientAssetsResponse>(
        `/client/${clientId}/assets`
      );
    },
    enabled: !!clientId,
    staleTime: 2 * 60 * 1000,
  });
}

interface UploadAssetParams {
  clientId: string;
  file: File;
  category: AssetCategory;
  tags: string[];
}

/**
 * Upload a new asset file for a client.
 * Sends multipart/form-data with the file, category, and tags.
 */
export function useUploadAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, file, category, tags }: UploadAssetParams) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      formData.append("tags", JSON.stringify(tags));

      return apiClient.postFormData(
        `/client/${clientId}/assets`,
        formData
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.client(variables.clientId),
      });
    },
  });
}

interface DeleteAssetParams {
  clientId: string;
  assetId: number;
}

/** Delete an asset by ID */
export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, assetId }: DeleteAssetParams) => {
      return apiClient.delete(`/client/${clientId}/assets/${assetId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.client(variables.clientId),
      });
    },
  });
}

interface UpdateTagsParams {
  clientId: string;
  assetId: number;
  tags: string[];
}

/** Update the tags on an existing asset */
export function useUpdateAssetTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, assetId, tags }: UpdateTagsParams) => {
      return apiClient.patch(
        `/client/${clientId}/assets/${assetId}/tags`,
        { tags }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.client(variables.clientId),
      });
    },
  });
}

/**
 * Download the original asset file via blob fetch + browser download.
 *
 * @param clientId - Client that owns the asset
 * @param assetId - The asset to download
 * @param filename - Original filename for the download
 */
export async function downloadOriginal(
  clientId: string,
  assetId: number,
  filename: string
): Promise<void> {
  const blob = await apiClient.getBlob(
    `/client/${clientId}/assets/${assetId}/file`
  );

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Download a ZIP of all generated sizes for a round asset.
 * Fetches the blob from the backend and triggers a browser download.
 *
 * @param clientId - Client that owns the asset
 * @param assetId - The round asset to generate sizes for
 * @param filename - Name for the downloaded ZIP file
 */
export async function downloadSizes(
  clientId: string,
  assetId: number,
  filename?: string
): Promise<void> {
  const blob = await apiClient.getBlob(
    `/client/${clientId}/assets/${assetId}/sizes`
  );

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || `asset-${assetId}-sizes.zip`;
  document.body.appendChild(anchor);
  anchor.click();

  // Cleanup
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
